import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppButton from "../button/component";
import History from "../../assets/footer-b/history.svg";
import HistoryGrey from "../../assets/footer-b/history-grey.svg";
import Invite from "../../assets/footer-b/invite.svg";
import InviteGrey from "../../assets/footer-b/invite-grey.svg";
import Awards from "../../assets/footer-b/awards.svg";
import AwardsGrey from "../../assets/footer-b/awards-grey.svg";
import Support from "../../assets/footer-b/support.svg";
import SupportGrey from "../../assets/footer-b/support-grey.svg";

/* FOOTER COMPONENT */

const Footer = ({
  className = "",
  buttonTitle = "",
  onClick = () => {},
  disabled,
  buttonIcon,
  buttonId,
  showError,
  errorMessage,
  onInviteClick,
  onPlusClick = () => {},
  onPlusClose = () => {},
}) => {
  const { pathname } = useLocation();
  const { locale } = useParams();
  const { t } = useTranslation();

  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  const [activeTab, setActiveTab] = useState(null);

  useEffect(() => {
    const resize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  // Hide Olark button on mobile when footer-b is present
  useEffect(() => {
    if (isMobile) {
      const hideOlarkButton = () => {
        const buttonSelectors = [
          ".olark-launch-button-wrapper",
          ".olark-launch-button",
          '[id*="olark-launch"]',
        ];

        buttonSelectors.forEach((selector) => {
          const elements = document.querySelectorAll(selector);
          elements.forEach((element) => {
            if (element) {
              element.style.setProperty("display", "none", "important");
            }
          });
        });
      };

      // Hide immediately and on a delay to catch late-loading Olark
      hideOlarkButton();
      const timer = setTimeout(hideOlarkButton, 1000);

      return () => clearTimeout(timer);
    }
  }, [isMobile]);

  const currentLocale = locale ? `/${locale}` : "";

  const handleSupportClick = () => {
    onPlusClose();
    setActiveTab("support");
    // Only handle clicks on mobile devices
    if (window.innerWidth >= 1024) {
      return; // Let desktop use the default Olark button
    }

    if (window.olark) {
      // Show only the Olark chat box on mobile
      const chatBoxSelectors = ["#olark-box", '[id*="olark-box"]'];

      chatBoxSelectors.forEach((selector) => {
        const elements = document.querySelectorAll(selector);
        elements.forEach((element) => {
          if (element) {
            element.style.setProperty("display", "block", "important");
            element.style.setProperty("position", "fixed", "important");
            element.style.setProperty("z-index", "9999999", "important");

            // Mobile positioning only
            element.style.setProperty("bottom", "120px", "important");
            element.style.setProperty("right", "10px", "important");
            element.style.setProperty("left", "10px", "important");
            element.style.setProperty(
              "width",
              "calc(100vw - 20px)",
              "important",
            );
            element.style.setProperty("max-width", "400px", "important");
            element.style.setProperty("max-height", "500px", "important");
          }
        });
      });

      // Add custom CSS for mobile chat styling
      if (!document.getElementById("custom-olark-mobile-chat-styles")) {
        const style = document.createElement("style");
        style.id = "custom-olark-mobile-chat-styles";
        style.innerHTML = `
          @media (max-width: 1023px) {
            #olark-box {
              box-shadow: 0 4px 20px rgba(0,0,0,0.15) !important;
              border-radius: 12px !important;
              overflow: hidden !important;
              margin: 0 auto !important;
            }
          }
        `;
        document.head.appendChild(style);
      }

      window.olark("api.box.show");
      window.olark("api.box.expand");
    }
  };
  const closeOlarkChat = () => {
    if (window.olark) {
      window.olark("api.box.shrink");
      window.olark("api.box.hide");
    }
  };

  /* ===========================
     MOBILE FOOTER
  =========================== */

  if (isMobile) {
    return (
      <footer
        className="fixed bottom-0 left-0 right-0 z-50 bg-transparent"
        style={{ height: isMobile ? "105px" : "11vh" }}
      >
        {/* WHITE BAR WITH U-SHAPED CUT */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox="0 0 1440 110"
          preserveAspectRatio="none"
        >
          <defs>
            <mask id="fab-notch-mask">
              <rect width="100%" height="100%" fill="white" />
              <path
                d="
                  M520,0
                  C580,0 600,40 720,40
                  C840,40 860,0 920,0
                  Z
                "
                fill="black"
              />
            </mask>
          </defs>

          <rect
            width="100%"
            height="100%"
            fill="white"
            mask="url(#fab-notch-mask)"
          />
        </svg>

        {/* ICONS ROW */}
        <div className="absolute bottom-0 left-0 right-0 flex justify-around items-center px-2 pb-1">
          {/* HISTORY */}
          <div
            onClick={() => {
              setActiveTab("history");
              closeOlarkChat();
              onPlusClose();
              if (isMobile) {
                const element = document.getElementById("assessment-table");
                if (element) {
                  element.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                }
              }
            }}
            className="flex flex-col items-center cursor-pointer"
          >
            <div className="w-9 h-9 flex items-center justify-center mt-2">
              <img
                src={activeTab === "history" ? History : HistoryGrey}
                alt="History"
                width={36}
                height={36}
              />
            </div>
            <span
              className={`mt-1 mb-[27px] ${
                activeTab === "history"
                  ? "text-insightful-purple font-normal"
                  : "text-insightful-gray-mobile-footer"
              }`}
              style={{ fontSize: "10px", fontFamily: "Arial, sans-serif" }}
            >
              {t("footer-b.history")}
            </span>
          </div>

          {/* INVITE */}
          <div
            onClick={() => {
              setActiveTab("invite");
              closeOlarkChat();
              onPlusClose();

              if (onInviteClick) onInviteClick();
            }}
            className="flex flex-col items-center cursor-pointer"
          >
            <div className="w-9 h-9 flex items-center justify-center mt-2">
              <img
                src={activeTab === "invite" ? Invite : InviteGrey}
                alt="Invite"
                width={36}
                height={36}
              />
            </div>

            <span
              className={`mt-1 mb-[27px] ${
                activeTab === "invite"
                  ? "text-insightful-purple font-normal"
                  : "text-insightful-gray-mobile-footer"
              }`}
              style={{ fontSize: "10px", fontFamily: "Arial, sans-serif" }}
            >
              {t("footer-b.invite")}
            </span>
          </div>

          {/* CENTER SPACE (FAB) */}
          <div className="flex flex-col items-center">
            <div className="w-9 h-9 mt-2" />
            <span
              onClick={() => {
                setActiveTab(null);
                closeOlarkChat();
                onPlusClick();
              }}
              className={`mt-1 mb-[27px] ${
                activeTab === null
                  ? "text-insightful-purple font-bold"
                  : "text-insightful-gray-mobile-footer font-bold"
              }`}
              style={{ fontSize: "10px" }}
            >
              {t("footer-b.new_test")}
            </span>
          </div>

          {/* AWARDS */}
          <div
            onClick={() => {
              setActiveTab("awards");
              closeOlarkChat();
              onPlusClose();
              if (isMobile) {
                const element = document.getElementById("achievements-card");
                if (element) {
                  element.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                }
              }
            }}
            className="flex flex-col items-center cursor-pointer"
          >
            <div className="w-9 h-9 flex items-center justify-center mt-2">
              <img
                src={activeTab === "awards" ? Awards : AwardsGrey}
                alt="Awards"
                width={36}
                height={36}
              />
            </div>
            <span
              className={`mt-1 mb-[27px] ${
                activeTab === "awards"
                  ? "text-insightful-purple font-normal"
                  : "text-insightful-gray-mobile-footer"
              }`}
              style={{ fontSize: "10px", fontFamily: "Arial, sans-serif" }}
            >
              {t("footer-b.awards")}
            </span>
          </div>
          <div
            className="flex flex-col items-center cursor-pointer hover:transform hover:scale-105 transition-transform duration-200"
            onClick={handleSupportClick}
          >
            <div className="w-9 h-9 flex items-center justify-center mt-2">
              <img
                src={activeTab === "support" ? Support : SupportGrey}
                alt="Support"
                width={36}
                height={36}
                className="object-contain"
                style={{
                  filter:
                    activeTab === "support"
                      ? "invert(27%) sepia(86%) saturate(1776%) hue-rotate(213deg) brightness(97%) contrast(91%)"
                      : "none",
                }}
              />
            </div>
            <span
              className={`mt-1 mb-[27px] ${
                activeTab === "support"
                  ? "text-insightful-purple font-normal"
                  : "text-insightful-gray-mobile-footer"
              }`}
              style={{ fontSize: "10px", fontFamily: "Arial, sans-serif" }}
            >
              {t("footer-b.support")}
            </span>
          </div>
        </div>

        {/* FLOATING + BUTTON */}
        <button
          onClick={() => {
            setActiveTab(null);
            onPlusClick();
          }}
          className="absolute left-1/2 -top-[50px] -translate-x-1/2
               w-[80px] h-[80px] rounded-full bg-white
               shadow-lg flex items-center justify-center"
        >
          <div className="w-[60px] h-[60px] rounded-full bg-purple-500 flex items-center justify-center text-white text-7xl">
            +
          </div>
        </button>
      </footer>
    );
  }

  /* ===========================
     WEB FOOTER (UNCHANGED)
  =========================== */

  return (
    <footer
      className={`border-t border-insightful-light-gray py-[10px] bg-white ${className}`}
    >
      <div className="text-center text-sm text-insightful-gray">
        <Link
          to={`https://insightfultraits.com${currentLocale}/privacy-policy`}
          target="_blank"
        >
          {t("footer.privacy_policy")}
        </Link>{" "}
        |{" "}
        <Link
          to={`https://insightfultraits.com${currentLocale}/terms-conditions`}
          target="_blank"
        >
          {t("footer.terms_and_conditions")}
        </Link>{" "}
        |{" "}
        <Link
          to={`https://insightfultraits.com${currentLocale}/contact`}
          target="_blank"
        >
          {t("footer.contact_us")}
        </Link>
        <p className="mt-1">
          © {new Date().getFullYear()}, {t("footer.insightfultraits_llc")}
        </p>
      </div>
    </footer>
  );
};

export default Footer;
